import React, { useState } from 'react';
import { ROUTES } from '../../routes';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';

// ── Variantes de animación (nivel módulo) ──────────────────────────────────
// animation-designer § Pattern 4 (Modal/Dialog): AnimatePresence para
// enter/exit real del panel y overlay. El panel desliza desde la izquierda.
// animation-designer § Pattern 2 (Staggered List): ítems del nav entran
// escalonados cuando el menú abre — staggerChildren: 0.06, delay: 0.15.

const overlayVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.25, ease: 'easeOut' } },
  exit: { opacity: 0, transition: { duration: 0.2, ease: 'easeIn' } }
};

const panelVariants = {
  hidden: { x: '-100%', opacity: 0 },
  show: { x: 0, opacity: 1, transition: { duration: 0.3, ease: 'easeOut' } },
  exit: { x: '-100%', opacity: 0, transition: { duration: 0.25, ease: 'easeIn' } }
};

const navContainer = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.15 } }
};

const navItem = {
  hidden: { opacity: 0, x: -8 },
  show: { opacity: 1, x: 0, transition: { duration: 0.2, ease: 'easeOut' } }
};
// ──────────────────────────────────────────────────────────────────────────

const Menu = ({ isOpen, closeMenu }) => {
  const { t } = useTranslation();
  const [isCharactersOpen, setIsCharactersOpen] = useState(false);
  const [isToolsOpen, setIsToolsOpen] = useState(false);

  // animation-designer § Reduce Motion — accesibilidad
  const shouldReduceMotion = useReducedMotion();

  const resolvedPanel = shouldReduceMotion
    ? { hidden: { opacity: 0 }, show: { opacity: 1 }, exit: { opacity: 0 } }
    : panelVariants;

  const resolvedNavItem = shouldReduceMotion
    ? { hidden: { opacity: 0 }, show: { opacity: 1, x: 0 } }
    : navItem;

  // Clases compartidas para todos los ítems principales
  // interface-design § States: default + hover + focus + active en cada ítem
  // interface-design § Dark Mode: borders sobre shadows para separación
  // La firma visual: border-l-2 border-transparent → hover:border-[#FAB0C0]
  // El acento lateral "enciende" en hover — emerge del mundo del logo.
  const itemBase = `
    flex items-center gap-3 w-full
    px-4 py-3 rounded-r-lg
    text-sm font-medium text-white/80
    border-l-2 border-transparent
    hover:bg-white/8 hover:text-white hover:border-[#FAB0C0]
    focus:outline-none focus:bg-white/8 focus:border-[#FAB0C0]
    transition-all duration-200
    cursor-pointer
  `;

  // Ítems de submenú — un nivel de elevación más bajo
  // interface-design § Surface Elevation: background más oscuro = más profundo
  const subItemBase = `
    flex items-center gap-3 w-full
    pl-10 pr-4 py-2 rounded-r-lg
    text-sm text-white/60
    border-l-2 border-transparent
    hover:bg-white/6 hover:text-white/90 hover:border-[#48E5B9]
    focus:outline-none focus:border-[#48E5B9]
    transition-all duration-200
    cursor-pointer
  `;

  return ReactDOM.createPortal(
    // AnimatePresence: permite animaciones de salida reales (exit)
    // animation-designer § Pattern 4 (Modal/Dialog Animation)
    <AnimatePresence>
      {isOpen && (
        <>
          {/* ── Overlay ──────────────────────────────────────────────────
              frontend-design § Backgrounds: backdrop-blur-sm sobre el
              overlay crea profundidad. No solo oscurece — desenfoca el
              contenido detrás, reforzando que el menú está "sobre" la UI. */}
          <motion.div
            key="menu-overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="show"
            exit="exit"
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
            onClick={closeMenu}
          />

          {/* ── Panel Lateral ─────────────────────────────────────────────
              interface-design § Navigation Context:
              "same background as canvas + border separation rather than
              different colors" → bg-black/95 + border-r
              interface-design § Dark Mode: border para definición,
              no shadow. backdrop-blur-xl para efecto glass consistente
              con el header. Coherencia de superficie en toda la app. */}
          <motion.aside
            key="menu-panel"
            variants={resolvedPanel}
            initial="hidden"
            animate="show"
            exit="exit"
            className="
              fixed top-0 left-0 h-full w-72
              bg-black/95 backdrop-blur-xl
              border-r border-white/10
              z-[70]
              flex flex-col
              overflow-hidden
            "
          >
            {/* Gradiente atmosférico — mismo mundo que el header
                frontend-design § Backgrounds & Visual Details:
                La paleta del logo filtra suavemente hacia el panel.
                Conexión visual entre header y menú = coherencia de producto */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#FAB0C0]/6 via-transparent to-[#48E5B9]/4 pointer-events-none -z-10" />

            {/* ── Header del Panel ─────────────────────────────────────── */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/8 flex-shrink-0">
              {/* interface-design § Typography: heading con tracking-wide,
                  muted = cuarto nivel de jerarquía de texto */}
              <span className="text-xs font-semibold tracking-widest uppercase text-white/40">
                {t('Menu.title')}
              </span>
              <motion.button
                onClick={closeMenu}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="
                  p-1.5 rounded-md text-white/40 hover:text-white
                  hover:bg-white/10 transition-colors
                  focus:outline-none focus:ring-2 focus:ring-[#FAB0C0]/50
                "
                aria-label="Cerrar menú"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </motion.button>
            </div>

            {/* ── Navegación ───────────────────────────────────────────── */}
            {/* animation-designer § Pattern 2: los ítems entran escalonados */}
            <motion.nav
              className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5"
              variants={navContainer}
              initial="hidden"
              animate="show"
            >
              {/* ─ Inicio ─────────────────────────────────────────────── */}
              <motion.div variants={resolvedNavItem}>
                <a href={ROUTES.MAIN} className={itemBase}>
                  {/* interface-design § Iconography: iconos que clarifican.
                      Primer set coherente en todo el menú (outline, 20px) */}
                  <svg className="w-5 h-5 flex-shrink-0 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  {t('Menu.link_main')}
                </a>
              </motion.div>

              {/* ─ Separador de sección: NAVEGAR ─────────────────────── */}
              {/* interface-design § Text Hierarchy: label muted de sección
                  = cuarto nivel de jerarquía. Crea estructura sin peso extra */}
              <motion.div variants={resolvedNavItem} className="pt-4 pb-1 px-4">
                <span className="text-[10px] font-bold tracking-widest uppercase text-white/25">
                  {t('Menu.section_navigate', 'Navegar')}
                </span>
              </motion.div>

              {/* ─ Tools (Dropdown) ───────────────────────────────────── */}
              <motion.div variants={resolvedNavItem}>
                <button
                  onClick={() => setIsToolsOpen(!isToolsOpen)}
                  className={`${itemBase} justify-between`}
                >
                  <span className="flex items-center gap-3">
                    <svg className="w-5 h-5 flex-shrink-0 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Tools
                  </span>
                  <motion.svg
                    className="w-4 h-4 text-white/40"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    animate={{ rotate: isToolsOpen ? 180 : 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </motion.svg>
                </button>

                {/* Submenú Tools — accordion con max-height CSS */}
                <AnimatePresence>
                  {isToolsOpen && (
                    <motion.ul
                      key="tools-sub"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1, transition: { duration: 0.25, ease: 'easeOut' } }}
                      exit={{ height: 0, opacity: 0, transition: { duration: 0.2, ease: 'easeIn' } }}
                      className="overflow-hidden mt-0.5 space-y-0.5"
                    >
                      <li>
                        <a href={ROUTES.TOOLS_SCORE} onClick={closeMenu} className={subItemBase}>
                          {t('Menu.tools_score')}
                        </a>
                      </li>
                      <li>
                        <a href={ROUTES.TOOLS_SUPPORT} onClick={closeMenu} className={subItemBase}>
                          {t('Menu.tools_support')}
                        </a>
                      </li>
                      <li>
                        <a href={ROUTES.TOOLS_SKILL_ANALYZER} onClick={closeMenu} className={subItemBase}>
                          {t('Menu.tools_skill_analyzer', 'Skill Analyzer')}
                        </a>
                      </li>
                      <li>
                        <a href={ROUTES.TOOLS_RACE_PLANNER} onClick={closeMenu} className={subItemBase}>
                          {t('Menu.tools_race_planner', 'Planeador de carreras')}
                        </a>
                      </li>
                    </motion.ul>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* ─ Characters (Dropdown) ──────────────────────────────── */}
              <motion.div variants={resolvedNavItem}>
                <button
                  onClick={() => setIsCharactersOpen(!isCharactersOpen)}
                  className={`${itemBase} justify-between`}
                >
                  <span className="flex items-center gap-3">
                    <svg className="w-5 h-5 flex-shrink-0 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {t('Menu.link_characters')}
                  </span>
                  <motion.svg
                    className="w-4 h-4 text-white/40"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    animate={{ rotate: isCharactersOpen ? 180 : 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </motion.svg>
                </button>

                {/* Submenú Characters */}
                <AnimatePresence>
                  {isCharactersOpen && (
                    <motion.ul
                      key="chars-sub"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1, transition: { duration: 0.25, ease: 'easeOut' } }}
                      exit={{ height: 0, opacity: 0, transition: { duration: 0.2, ease: 'easeIn' } }}
                      className="overflow-hidden mt-0.5 space-y-0.5"
                    >
                      <li>
                        <a href={ROUTES.CHARACTERS_ORIGINAL} onClick={closeMenu} className={subItemBase}>
                          {t('Menu.link_original')}
                        </a>
                      </li>
                      <li>
                        <a href={ROUTES.CHARACTERS_SUPPORT} onClick={closeMenu} className={subItemBase}>
                          {t('Menu.link_support')}
                        </a>
                      </li>
                    </motion.ul>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* ─ Skills ─────────────────────────────────────────────── */}
              <motion.div variants={resolvedNavItem}>
                <a href={ROUTES.SKILLS} className={itemBase}>
                  <svg className="w-5 h-5 flex-shrink-0 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  {t('Menu.link_skills')}
                </a>
              </motion.div>

              {/* ─ Races ──────────────────────────────────────────────── */}
              <motion.div variants={resolvedNavItem}>
                <a href={ROUTES.RACES} className={itemBase}>
                  <svg className="w-5 h-5 flex-shrink-0 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                  </svg>
                  {t('Menu.link_races')}
                </a>
              </motion.div>

              {/* ─ Separador de sección: EXTRA ────────────────────────── */}
              <motion.div variants={resolvedNavItem} className="pt-4 pb-1 px-4">
                <span className="text-[10px] font-bold tracking-widest uppercase text-white/25">
                  {t('Menu.section_extra', 'Extra')}
                </span>
              </motion.div>

              {/* ─ Mini Games ─────────────────────────────────────────────
                  interface-design § Sameness: mismo estilo que el resto.
                  El emoji 🎮 actúa como identificador visual suficiente.
                  NO necesita text-purple-300 font-bold para distinguirse.
                  El acento lateral y el icono son la firma correcta. */}
              <motion.div variants={resolvedNavItem}>
                <a href={ROUTES.MINIGAMES} onClick={closeMenu} className={itemBase}>
                  <svg className="w-5 h-5 flex-shrink-0 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                  {t('Menu.link_minigames', 'Mini Games')}
                </a>
              </motion.div>

            </motion.nav>

            {/* ── Footer del Panel ─────────────────────────────────────── */}
            {/* frontend-design § Color: un toque del acento turquesa en el
                footer cierra visualmente el panel — la paleta del logo
                aparece arriba (header) y abajo (footer del menú). */}
            <div className="flex-shrink-0 px-5 py-4 border-t border-white/8">
              <div className="h-0.5 w-8 rounded-full bg-gradient-to-r from-[#FAB0C0] to-[#48E5B9] mx-auto opacity-40" />
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default Menu;
// Revisado: 2025-12-18 [I18n Scan]
